import {
  FieldWrapper,
  Icons,
  Input,
  Label,
  useToggle,
} from '@commenda/kakashi';
import { TRedirectStepDefinition, TValueProvider } from '@commenda/types';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { VariablesBindingModal } from '../modals/VariablesBindingModal';

const TEMPLATE_VAR_RE = /\{\{([^}]+)\}\}/g;
const COMMENDA_ROOT_DOMAIN = 'commenda.io';

type UrlValidation =
  | { status: 'valid'; isRelative: boolean }
  | { status: 'commenda-subdomain' }
  | { status: 'invalid-format' };

function extractUrlVariables(url: string): string[] {
  const matches = Array.from(url.matchAll(TEMPLATE_VAR_RE));
  return Array.from(new Set(matches.map((m) => m[1].trim())));
}

function validateRedirectUrl(rawUrl: string): UrlValidation {
  // Strip template vars so the URL constructor doesn't choke on {{key}}
  const url = rawUrl.replace(TEMPLATE_VAR_RE, 'PLACEHOLDER');

  // Relative: starts with / but not //  (protocol-relative would be cross-domain)
  if (/^\/(?!\/)/.test(url)) return { status: 'valid', isRelative: true };

  if (!/^https?:\/\//i.test(url)) return { status: 'invalid-format' };

  try {
    const { hostname } = new URL(url);
    if (hostname.endsWith(`.${COMMENDA_ROOT_DOMAIN}`))
      return { status: 'commenda-subdomain' };
    return { status: 'valid', isRelative: false };
  } catch {
    return { status: 'invalid-format' };
  }
}

export const RedirectNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();
  const bindingModalVisibility = useToggle(false);
  const [editingVar, setEditingVar] = useState<string | null>(null);

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TRedirectStepDefinition;

  if (!selectedStep) return <div>Step not found</div>;

  const updateRedirectStep = (updates: Partial<TRedirectStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const handleUrlChange = (newUrl: string) => {
    const variables = extractUrlVariables(newUrl);
    const existing = selectedStep.valueMapping ?? {};
    const merged: Record<string, TValueProvider> = {};
    variables.forEach((v) => {
      if (existing[v]) merged[v] = existing[v];
    });
    updateRedirectStep({ redirectUrl: newUrl, valueMapping: merged });
  };

  const openBindingModal = (varName: string) => {
    setEditingVar(varName);
    bindingModalVisibility.show();
  };

  const onSaveBinding = (varName: string, value: TValueProvider) => {
    updateRedirectStep({
      valueMapping: { ...(selectedStep.valueMapping ?? {}), [varName]: value },
    });
  };

  const variables = extractUrlVariables(selectedStep.redirectUrl ?? '');
  const valueMapping = selectedStep.valueMapping ?? {};
  const missingBindings = variables.filter((v) => !valueMapping[v]);

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper>
        <Label>Redirect URL</Label>
        <Input
          value={selectedStep.redirectUrl ?? ''}
          placeholder="https://example.com/{{orgId}}/page"
          onChange={(e) => handleUrlChange(e.target.value)}
        />
        {(() => {
          const url = selectedStep.redirectUrl ?? '';
          if (!url) return null;
          const validation = validateRedirectUrl(url);
          if (validation.status === 'commenda-subdomain') {
            return (
              <p className="txt-extra-small-regular text-text-error mt-1">
                Please use a relative URL instead (e.g.{' '}
                <code>/path/to/page</code>).
              </p>
            );
          }
          if (validation.status === 'invalid-format') {
            return (
              <p className="txt-extra-small-regular text-text-error mt-1">
                Invalid URL. Use a relative path (e.g. <code>/path</code>) or a
                full URL starting with <code>https://</code>.
              </p>
            );
          }
          if (validation.isRelative) {
            return (
              <p className="txt-extra-small-regular text-text-secondary mt-1">
                Relative URL — domain will be prepended at runtime.
              </p>
            );
          }
          return (
            <p className="txt-extra-small-regular text-text-secondary mt-1">
              Use {`{{key}}`} to insert dynamic values into the URL.
            </p>
          );
        })()}
      </FieldWrapper>

      {variables.length > 0 && (
        <FieldWrapper>
          <Label>URL Variable Bindings</Label>
          <div className="flex flex-col gap-2">
            {variables.map((v) => (
              <div key={v} className="flex items-center justify-between gap-2">
                <span className="txt-small-regular text-text-primary font-mono">
                  {v}
                </span>
                <Icons.Edit01
                  className="size-4 cursor-pointer"
                  onClick={() => openBindingModal(v)}
                />
              </div>
            ))}
          </div>

          {missingBindings.length > 0 && (
            <p className="txt-extra-small-regular text-text-error mt-1">
              Missing binding for: {missingBindings.join(', ')}
            </p>
          )}
        </FieldWrapper>
      )}

      <VariablesBindingModal
        isOpen={bindingModalVisibility.visible}
        onClose={() => {
          bindingModalVisibility.hide();
          setEditingVar(null);
        }}
        variableName={editingVar}
        value={editingVar ? (valueMapping[editingVar] ?? null) : null}
        onSave={onSaveBinding}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />
    </div>
  );
};
